import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { users } from '@/api/client'
import { useAuth } from '@/hooks/useAuth'
import { Plus, Loader2, ShieldCheck, Eye, Wrench, Crown, X, Power } from 'lucide-react'
import { formatRelative } from '@/lib/utils'

const roleConfig: Record<string, { icon: any; cls: string; label: string }> = {
  Owner: { icon: Crown, cls: 'badge-warning', label: 'Owner' },
  Operator: { icon: Wrench, cls: 'badge-info', label: 'Operador' },
  Viewer: { icon: Eye, cls: 'badge-neutral', label: 'Visualizador' },
}

export function UsersPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showForm, setShowForm] = useState(false)
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<'Operator' | 'Viewer'>('Operator')

  const isOwner = user?.role === 'Owner'

  const list = useQuery({ queryKey: ['users'], queryFn: () => users.list() })

  const create = useMutation({
    mutationFn: () => users.create({ email, displayName, password, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowForm(false)
      setEmail(''); setDisplayName(''); setPassword(''); setRole('Operator')
    },
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => users.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  const activate = useMutation({
    mutationFn: (id: string) => users.activate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    create.mutate()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Operadores</h1>
          <p className="text-sm text-slate-500 mt-1">Pessoas com acesso ao M365 Transfer</p>
        </div>
        {isOwner && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus className="h-4 w-4" /> Novo operador
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="card p-6 mb-6 space-y-4">
          <h2 className="font-semibold text-slate-900">Novo operador</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">E-mail</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="label">Nome</label>
              <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            </div>
          </div>

          <div>
            <label className="label">Senha (mínimo 12 caracteres)</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={12} />
          </div>

          <div>
            <label className="label">Role</label>
            <select className="input max-w-xs" value={role} onChange={(e) => setRole(e.target.value as any)}>
              <option value="Operator">Operador (acesso completo, não administra usuários)</option>
              <option value="Viewer">Visualizador (apenas leitura)</option>
            </select>
          </div>

          {create.isError && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              {(create.error as any)?.message || 'Erro ao criar'}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" className="btn-primary" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Criar
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase text-slate-500">
              <th className="px-4 py-2 font-medium">Nome</th>
              <th className="px-4 py-2 font-medium">E-mail</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">2FA</th>
              <th className="px-4 py-2 font-medium">Último login</th>
              {isOwner && <th className="px-4 py-2 font-medium">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.data?.map((u) => {
              const cfg = roleConfig[u.role] || roleConfig.Viewer
              const Icon = cfg.icon
              return (
                <tr key={u.id}>
                  <td className="px-4 py-3 font-medium">{u.displayName}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={cfg.cls + ' inline-flex items-center gap-1'}>
                      <Icon className="h-3 w-3" /> {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.isActive ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Desativado</span>}
                  </td>
                  <td className="px-4 py-3">
                    {u.twoFactorEnabled
                      ? <ShieldCheck className="h-4 w-4 text-green-600" />
                      : <X className="h-4 w-4 text-slate-300" />}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatRelative(u.lastLoginAt)}</td>
                  {isOwner && (
                    <td className="px-4 py-3">
                      {u.role !== 'Owner' && (
                        u.isActive ? (
                          <button onClick={() => deactivate.mutate(u.id)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                            <Power className="h-3 w-3" /> Desativar
                          </button>
                        ) : (
                          <button onClick={() => activate.mutate(u.id)} className="text-xs text-green-600 hover:underline inline-flex items-center gap-1">
                            <Power className="h-3 w-3" /> Ativar
                          </button>
                        )
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
